import { useSelector } from 'react-redux';
import IPDayActions from './Components/IPDayActions';
import CycleData from './Components/CycleData';
import CalendarDayMenuInfoBox from './Components/CalendarDayMenuInfoBox';
import OvulationTestsList from './Components/OvulationTestsList';
import SharedOvulationPredictions from './Components/SharedOvulationPredictions';
import EventsList from './Components/EventsList';
import InputTimed from '../../components/InputTimed';
import useUpdateCalendarDay from '../../hooks/useUpdateCalendarDay';

/**
 * DayMenuContent - Content for the calendar day detail menu
 * Shows different UI based on user role (IP vs Donor) and ownership
 *
 * Props:
 * - dayData: Calendar data for the selected day (ipData, events, ovulationTests, note, etc.)
 * - role: User role ('ip' or 'donor')
 * - date: Selected date string (YYYY-MM-DD)
 * - noteText: Local state for note input (controlled by parent)
 * - setNoteText: Function to update note text
 * - userId: Optional user ID (if viewing another user's calendar)
 */
function DayMenuContent({ dayData, role, date, noteText, setNoteText, userId }) {
  // Get current user ID from Redux to determine if viewing own calendar
  const currentUserId = useSelector((s) => s.user.profile?.id);
  // Check if this is the own user's calendar (no userId provided or matches current user)
  const isOwnUser = !userId || userId === currentUserId;
  // Extract relevant data from the day's calendar data
  const { ipData, events, sharedOvulationPredictions, ovulationTests, note } = dayData || {};

  // Enrich tests with cycle day from ipData
  const enrichedTests = ovulationTests?.map(test => ({
    ...test,
    cycleDay: ipData?.cd
  })) || [];
  
  // Handler for note input changes
  const handleNoteChange = setNoteText || (() => {});

  // Calendar day update hook
  const { updateCalendarDay } = useUpdateCalendarDay();

  return (
    <div className="flex-1 flex flex-col px-5 overflow-hidden">
      {/* ── IP VIEW ── */}
      {role === 'ip' && (
        <>
          {/* Period day toggle + insemination button - two main actions for IP users */}
          <div className="flex-shrink-0">
            <IPDayActions ipData={ipData} date={date} />
          </div>

          {/* Calendar day info box - shows cycle day and test recommendation */}
          <div className="flex-shrink-0">
            <CalendarDayMenuInfoBox ipData={ipData} date={date} />
          </div>

          {/* Display calculated cycle data (predicted ovulation day, peak test day, cycle day number) */}
          <div className="flex-shrink-0">
            <CycleData ipData={ipData} />
          </div>

          {/* Dynamic sections: ovulation tests + shared predictions fill remaining height */}
          {/* If only one section has content, it takes full height; if both, they split evenly */}
          <div className="flex-1 flex flex-col min-h-0 gap-2">
            <OvulationTestsList tests={enrichedTests} />
            <SharedOvulationPredictions predictions={sharedOvulationPredictions} />
          </div>

          {/* Note input - only editable for own user, read-only for others */}
          {isOwnUser && (
            <div className="flex-shrink-0">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-primary">
                Note
              </p>
              <InputTimed
                type="textarea"
                defaultValue={note || ''}
                onCommit={(value) => {
                  updateCalendarDay(date, 'note', value);
                }}
                placeholder="Notes for this day..."
                className="min-h-20 w-full resize-none rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          )}
        </>
      )}

      {/* ── DONOR VIEW ── */}
      {role === 'donor' && (
        <div className="flex-1 flex flex-col min-h-0 gap-2 bg-gray-100/50 rounded-xl p-2">
          {/* Events section - green bg, fills remaining height if events exist */}
          {isOwnUser && (
            <div className={`${events?.length ? 'flex-1' : 'flex-shrink-0'} flex flex-col min-h-0 bg-white rounded-xl p-2`}>
              <EventsList events={events} date={date} />
            </div>
          )}
          {/* Shared ovulation predictions - purple bg, fills remaining height if no events */}
          {sharedOvulationPredictions?.length > 0 && (
            <div className="flex-1 flex flex-col min-h-0 bg-white rounded-xl p-2">
              <SharedOvulationPredictions predictions={sharedOvulationPredictions} />
            </div>
          )}

          {/* Note input - only editable for own user */}
          {isOwnUser && (
            <div className="flex-shrink-0">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-primary">
                Note
              </p>
              <InputTimed
                type="textarea"
                defaultValue={note || ''}
                onCommit={(value) => {
                  updateCalendarDay(date, 'note', value);
                }}
                placeholder="Notes for this day..."
                className="min-h-20 w-full resize-none rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default DayMenuContent;
